import { useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import i18n from "i18next";

/**
 * Ensures in-app notifications exist for subscription milestones:
 * - 7 days before subscription_end
 * - 1 day before subscription_end
 * - When the subscription has expired
 *
 * Idempotent via the (user_id, dedupe_key) UNIQUE constraint on the
 * `notifications` table — running on every login will not produce duplicates.
 */
export function useSubscriptionNotifications() {
  const { user, subscription } = useAuth();

  useEffect(() => {
    if (!user || !subscription) return;
    const isTr = i18n.language?.startsWith("tr");
    const ensure = async (key: string, type: string, title: string, body: string, link = "/premium") => {
      // upsert with onConflict respects the unique key, so existing rows are
      // left untouched (we don't want to overwrite a user's read_at flag).
      await supabase
        .from("notifications")
        .upsert(
          { user_id: user.id, type, title, body, link, dedupe_key: key },
          { onConflict: "user_id,dedupe_key", ignoreDuplicates: true },
        );
    };

    const endIso = subscription.subscription_end as string | undefined;
    if (!endIso) return;

    const end = new Date(endIso);
    if (Number.isNaN(end.getTime())) return;
    const dayKey = end.toISOString().slice(0, 10);
    const daysLeft = Math.ceil((end.getTime() - Date.now()) / 86_400_000);

    if (subscription.subscribed) {
      if (daysLeft === 7) {
        ensure(
          `subscription:expiring-7d:${dayKey}`,
          "subscription_expiring",
          isTr ? "Aboneliğinin bitmesine 7 gün kaldı" : "7 days left on your subscription",
          isTr
            ? `${subscription.plan_name ?? "Premium"} planın ${end.toLocaleDateString("tr-TR")} tarihinde sona eriyor.`
            : `Your ${subscription.plan_name ?? "Premium"} plan ends on ${end.toLocaleDateString("en-US")}.`,
        );
      }
      if (daysLeft <= 1 && daysLeft >= 0) {
        ensure(
          `subscription:last-day:${dayKey}`,
          "subscription_last_day",
          isTr ? "Son gün: aboneliğin bugün sona eriyor" : "Last day: your subscription ends today",
          isTr
            ? "Erişiminin kesilmemesi için planını şimdi yenile."
            : "Renew now to keep uninterrupted access.",
        );
      }
    } else {
      ensure(
        `subscription:expired:${dayKey}`,
        "subscription_expired",
        isTr ? "Aboneliğin sona erdi" : "Your subscription has expired",
        isTr
          ? "Premium içeriklere erişimini geri kazanmak için planını yenile."
          : "Renew your plan to regain premium access.",
      );
    }
  }, [user, subscription]);
}
